///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, {useState, useEffect, useRef} from 'react'
import { Helmet } from 'react-helmet-async'
import graphLogoImg from '../images/graph.png'

import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleCheck, faX, faArrowRight,
    faFileExcel, faFileCode, faCalculator,
    faMoneyBillTransfer, faShieldHalved, faListCheck,
    faSearch, faBolt, faUsers, faFileCircleCheck
} from '@fortawesome/free-solid-svg-icons'
import ReactTooltip from 'react-tooltip';
import { subDisplayData } from '../services/SubscriptionsService'
import { formatNr } from '../services/FormatService'
import ModalSetValue from './MainAppComponents/ModalSetValue'
import { logEvent } from "firebase/analytics"
import { analytics } from '../services/FirebaseService'

const WeclomePage = () => {

    const currentYear = new Date().getFullYear();
    const [totalSubs] = useState(subDisplayData)
    const navigate = useNavigate()
    const [modal, setModal] = useState(false)

    // Animate elements on scroll
    const [visible, setVisible] = useState(new Set())
    const observerRef = useRef(null)

    useEffect(() => {
        observerRef.current = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setVisible(prev => new Set([...prev, entry.target.dataset.wpReveal]))
                }
            })
        }, { threshold: 0.15 })

        document.querySelectorAll('[data-wp-reveal]').forEach(el => {
            observerRef.current.observe(el)
        })

        return () => observerRef.current?.disconnect()
    }, [])

    const revealClass = (id) => visible.has(id) ? 'wp-revealed' : ''

    const getOldPrice = (price) => {
        return (parseInt((+price) * (50/100) + (+price)) - 1) + .99
    }

    const DEMO_PLAN = {
        name: 'Demo',
        price: 0,
        lines: 1500,
        time: 3,
        options: { exchange: true, vat: true, layouts: 2, companies: 1, valStat: true }
    }

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Program Intrastat",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "url": "https://programintrastat.ro",
        "description": `Program pentru generarea declarației Intrastat ${currentYear}. Transformă fișierele Excel în XML compatibil cu platforma oficială INTRASTAT. Calcul valoare statistică, conversie valutară BNR, validare coduri TVA.`,
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "EUR",
            "description": "Demo gratuit 3 luni cu toate funcționalitățile incluse"
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "150"
        }
    }

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "Ce este Intrastat?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Intrastat este sistemul de colectare a datelor statistice privind comerțul cu bunuri între statele membre ale Uniunii Europene, operațional de la 1 ianuarie 1993."
                }
            },
            {
                "@type": "Question",
                "name": "Cine declară Intrastat?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Operatorii economici înregistrați în scopuri de TVA care realizează schimburi de bunuri cu alte state membre UE și depășesc pragurile valorice Intrastat."
                }
            },
            {
                "@type": "Question",
                "name": "Când trebuie transmis Intrastat?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Termenul limită este data de 15 a lunii următoare celei de referință."
                }
            }
        ]
    }

    const renderFeatureCheck = (has) => (
        <span className={`wp-check ${has ? 'wp-check--yes' : 'wp-check--no'}`}>
            <FontAwesomeIcon icon={has ? faCircleCheck : faX} />
        </span>
    )

    const renderPricingCard = (plan, index, isDemo = false) => (
        <div
            className={`wp-price-card ${isDemo ? 'wp-price-card--demo' : ''}`}
            key={isDemo ? 'demo' : index}
            onClick={() => navigate('/register')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/register')}
        >
            {isDemo && <div className="wp-price-badge">3 luni GRATUIT</div>}
            <h3 className="wp-price-card__name">{plan.name}</h3>
            {!isDemo && (
                <div className="wp-price-card__old">
                    €{getOldPrice(plan.price)}/lună
                </div>
            )}
            <div className="wp-price-card__amount">
                {isDemo ? (
                    <>
                        <span className="wp-price-card__currency">€</span>
                        <span className="wp-price-card__number">0</span>
                        <span className="wp-price-card__period">/3 luni</span>
                    </>
                ) : (
                    <>
                        <span className="wp-price-card__currency">€</span>
                        <span className="wp-price-card__number">{plan.price}</span>
                        <span className="wp-price-card__period">/lună</span>
                    </>
                )}
            </div>
            <ul className="wp-price-card__features">
                <li><strong>{isDemo ? 1 : plan.options.companies}</strong> {plan.options?.companies > 1 ? 'firme' : 'firmă'}</li>
                <li><strong>{formatNr(plan.lines)}</strong> linii/lună</li>
                <li><strong>{plan.options.layouts}</strong> machete</li>
                <li>
                    Valoare statistică {renderFeatureCheck(isDemo || plan.options.valStat)}
                </li>
                <li>
                    Verificare CIF/VAT {renderFeatureCheck(isDemo || plan.options.vat)}
                </li>
                <li>
                    Conversie valutară BNR {renderFeatureCheck(isDemo || plan.options.exchange)}
                </li>
            </ul>
            <div className="wp-price-card__cta">
                {isDemo ? 'Începe gratuit' : 'Alege planul'}
                <FontAwesomeIcon icon={faArrowRight} />
            </div>
        </div>
    )

    return (
    <>
    <Helmet>
        <title>{`Program Generare Intrastat ${currentYear} Gratuit | Din Excel în XML | Coduri NC`}</title>
        <meta name="description" content={`Program intrastat ${currentYear} online gratuit. Generează declarația Intrastat din Excel în XML. Calcul valoare statistică, conversie valutară BNR, validare coduri TVA. 3 luni GRATUIT.`} />
        <meta name="keywords" content={`intrastat, program intrastat, program generare intrastat, coduri intrastat, declaratie intrastat, intrastat ${currentYear}, coduri NC, intrastat online, intrastat gratuit, program intrastat gratuit, generare XML intrastat, coduri vamale intrastat`} />
        <link rel="canonical" href="https://programintrastat.ro" />
        <meta property="og:title" content={`Program Intrastat ${currentYear} — Generare declarație Intrastat din Excel`} />
        <meta property="og:description" content={`Generează declarația Intrastat ${currentYear} rapid și simplu. Din Excel în XML. 3 luni gratuit, toate funcționalitățile incluse.`} />
        <meta property="og:url" content="https://programintrastat.ro" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
    </Helmet>

    <main className="wp-page">

        {/* ===== HERO ===== */}
        <section className="wp-hero">
            <div className="wp-hero__bg" aria-hidden="true">
                <div className="wp-hero__grid-line wp-hero__grid-line--1"></div>
                <div className="wp-hero__grid-line wp-hero__grid-line--2"></div>
                <div className="wp-hero__grid-line wp-hero__grid-line--3"></div>
                <div className="wp-hero__accent-blob"></div>
            </div>

            <div className="wp-hero__content">
                <div className="wp-hero__text">
                    <div className="wp-hero__eyebrow">
                        <span className="wp-hero__dot"></span>
                        Platformă online Intrastat {currentYear}
                    </div>
                    <h1 className="wp-hero__title" title={`Program Intrastat ${currentYear}`}>
                        Generează declarația<br />
                        <span className="wp-hero__title-accent">Intrastat</span> din Excel
                    </h1>
                    <p className="wp-hero__subtitle">
                        Transformă fișierul Excel în XML compatibil cu platforma oficială INTRASTAT.
                        Calcul valoare statistică, conversie valutară BNR, validare coduri TVA — totul automatizat.
                    </p>
                    <div className="wp-hero__demo-callout">
                        <div className="wp-hero__demo-tag">GRATUIT</div>
                        <div className="wp-hero__demo-text">
                            <strong>3 luni demo complet</strong> — toate funcționalitățile incluse, fără card bancar
                        </div>
                    </div>
                    <div className="wp-hero__actions">
                        <button className="wp-btn wp-btn--primary" onClick={() => navigate('/register')}>
                            Începe gratuit
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                        <button className="wp-btn wp-btn--ghost" onClick={() => {
                            document.getElementById('wp-how')?.scrollIntoView({behavior: 'smooth'})
                        }}>
                            Cum funcționează?
                        </button>
                    </div>
                </div>
                <div className="wp-hero__visual">
                    <div className="wp-hero__card-stack">
                        <div className="wp-hero__mock wp-hero__mock--excel">
                            <div className="wp-hero__mock-bar">
                                <span></span><span></span><span></span>
                            </div>
                            <div className="wp-hero__mock-grid">
                                {[...Array(12)].map((_, i) => (
                                    <div key={i} className="wp-hero__mock-cell"></div>
                                ))}
                            </div>
                            <div className="wp-hero__mock-label">.xlsx</div>
                        </div>
                        <div className="wp-hero__mock-arrow" aria-hidden="true">
                            <FontAwesomeIcon icon={faArrowRight} />
                        </div>
                        <div className="wp-hero__mock wp-hero__mock--xml">
                            <div className="wp-hero__mock-bar">
                                <span></span><span></span><span></span>
                            </div>
                            <div className="wp-hero__mock-code">
                                <span>&lt;Intrastat&gt;</span>
                                <span>&nbsp;&nbsp;&lt;declaratie&gt;</span>
                                <span>&nbsp;&nbsp;&nbsp;&nbsp;&lt;linie /&gt;</span>
                                <span>&nbsp;&nbsp;&lt;/declaratie&gt;</span>
                                <span>&lt;/Intrastat&gt;</span>
                            </div>
                            <div className="wp-hero__mock-label">.xml</div>
                        </div>
                    </div>
                    <img src={graphLogoImg} alt="Program Intrastat" className="wp-hero__logo" />
                </div>
            </div>
        </section>

        {/* ===== TRUST BAR ===== */}
        <section className="wp-trust" aria-label="Avantaje cheie">
            <div className="wp-trust__item">
                <FontAwesomeIcon icon={faBolt} />
                <span>Generare sub 1 minut</span>
            </div>
            <div className="wp-trust__divider"></div>
            <div className="wp-trust__item">
                <FontAwesomeIcon icon={faShieldHalved} />
                <span>Date securizate</span>
            </div>
            <div className="wp-trust__divider"></div>
            <div className="wp-trust__item">
                <FontAwesomeIcon icon={faListCheck} />
                <span>Nomenclator NC la zi</span>
            </div>
            <div className="wp-trust__divider"></div>
            <div className="wp-trust__item">
                <FontAwesomeIcon icon={faUsers} />
                <span>Declaranți terți</span>
            </div>
        </section>

        {/* ===== NC CODE SEARCH ===== */}
        <section className="wp-nc" id="lista-coduri-intrastat" data-wp-reveal="nc" aria-label={`Coduri Intrastat NC ${currentYear}`}>
            <div className={`wp-nc__inner ${revealClass('nc')}`}>
                <div className="wp-nc__text">
                    <h2 title={`Coduri Intrastat ${currentYear}`}>
                        Cauți coduri Intrastat NC {currentYear}?
                    </h2>
                    <p>Nomenclatorul complet de coduri vamale Intrastat, actualizat permanent.</p>
                </div>
                <button className="wp-btn wp-btn--outline" onClick={() => {
                    logEvent(analytics, 'search_nc_code_main_page');
                    setModal(true);
                }}>
                    <FontAwesomeIcon icon={faSearch} />
                    Caută coduri NC {currentYear}
                </button>
            </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className="wp-how" id="wp-how" data-wp-reveal="how" aria-labelledby="wp-how-title">
            <div className={`wp-how__header ${revealClass('how')}`}>
                <h2 id="wp-how-title">Cum funcționează?</h2>
                <p>De la fișierul Excel la declarația XML în 3 pași simpli</p>
            </div>
            <div className="wp-how__steps" data-wp-reveal="steps">
                <div className={`wp-how__step ${revealClass('steps')}`}>
                    <div className="wp-how__step-num">01</div>
                    <div className="wp-how__step-icon"><FontAwesomeIcon icon={faFileExcel} /></div>
                    <h3>Importă fișierul Excel</h3>
                    <p>
                        Încărcați fișierul .xlsx cu datele tranzacțiilor. Platforma recunoaște automat coloanele necesare.
                        {' '}<a href="https://landing.programintrastat.ro/ce-declar-la-intrastat" target="_blank" rel="noopener noreferrer">
                            Ce trebuie să conțină fișierul?
                        </a>
                    </p>
                </div>
                <div className={`wp-how__step ${revealClass('steps')}`} style={{animationDelay: '0.15s'}}>
                    <div className="wp-how__step-num">02</div>
                    <div className="wp-how__step-icon"><FontAwesomeIcon icon={faCalculator} /></div>
                    <h3>Procesare automată</h3>
                    <p>
                        Se calculează valoarea statistică, se convertesc valorile valutare după cursul BNR,
                        se validează codurile TVA prin VIES, se verifică erorile.
                    </p>
                </div>
                <div className={`wp-how__step ${revealClass('steps')}`} style={{animationDelay: '0.3s'}}>
                    <div className="wp-how__step-num">03</div>
                    <div className="wp-how__step-icon"><FontAwesomeIcon icon={faFileCode} /></div>
                    <h3>Generare XML</h3>
                    <p>
                        Se generează fișierul XML compatibil cu platforma oficială INTRASTAT.
                        Puteți edita, corecta erorile și regenera oricând.
                    </p>
                </div>
            </div>
        </section>

        {/* ===== XML VALIDATOR (free tool, no account needed) ===== */}
        <section className="wp-nc" id="verificare-xml" data-wp-reveal="nc" aria-label="Verificare fișier XML Intrastat">
            <div className={`wp-nc__inner ${revealClass('nc')}`}>
                <div className="wp-nc__text">
                    <h2>Aplicația INTRASTAT îți respinge fișierul XML?</h2>
                    <p>
                        Verifică gratuit unde este problema, fără cont. Fișierul este analizat
                        în browser și nu părăsește calculatorul tău.
                    </p>
                </div>
                <button className="wp-btn wp-btn--outline" onClick={() => {
                    logEvent(analytics, 'open_xml_validator_main_page');
                    navigate('/verificare-xml-intrastat');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}>
                    <FontAwesomeIcon icon={faFileCircleCheck} />
                    Verifică fișierul XML
                </button>
            </div>
        </section>

        {/* ===== FEATURES ===== */}
        <section className="wp-features" data-wp-reveal="feat" aria-labelledby="wp-feat-title">
            <div className={`wp-features__header ${revealClass('feat')}`}>
                <h2 id="wp-feat-title">Ce include platforma?</h2>
            </div>
            <div className="wp-features__grid" data-wp-reveal="featgrid">
                {[
                    { icon: faFileCode, title: 'Generare XML Intrastat', desc: 'Fișiere XML compatibile cu platforma oficială INTRASTAT, generate automat din datele importate' },
                    { icon: faCalculator, title: 'Calcul valoare statistică', desc: 'Calcul automat și configurabil al valorii statistice conform regulilor în vigoare' },
                    { icon: faMoneyBillTransfer, title: 'Conversie valutară BNR', desc: 'Conversii automate folosind cursul oficial BNR din data facturii' },
                    { icon: faListCheck, title: 'Validare coduri TVA (VIES)', desc: 'Verificarea automată a codurilor TVA/CIF prin baza de date oficială europeană VIES' },
                    { icon: faSearch, title: `Nomenclator coduri NC ${currentYear}`, desc: 'Lista completă a codurilor vamale Intrastat, actualizată permanent' },
                    { icon: faShieldHalved, title: 'Identificare și corectare erori', desc: 'Erorile sunt evidențiate în timp real cu sugestii de corectare, direct în tabel' },
                ].map((feat, i) => (
                    <div className={`wp-features__item ${revealClass('featgrid')}`} key={i} style={{animationDelay: `${i * 0.08}s`}}>
                        <div className="wp-features__icon">
                            <FontAwesomeIcon icon={feat.icon} />
                        </div>
                        <h3>{feat.title}</h3>
                        <p>{feat.desc}</p>
                    </div>
                ))}
            </div>
        </section>

        {/* ===== CTA BANNER ===== */}
        <section className="wp-cta-banner" data-wp-reveal="cta1" aria-label="Înregistrare gratuită">
            <div className={`wp-cta-banner__inner ${revealClass('cta1')}`}>
                <div>
                    <h2>Încearcă platforma 3 luni, gratuit</h2>
                    <p>Toate funcționalitățile incluse. Fără card bancar. Fără obligații.</p>
                </div>
                <button className="wp-btn wp-btn--primary wp-btn--lg" onClick={() => navigate('/register')}>
                    Creează cont gratuit
                    <FontAwesomeIcon icon={faArrowRight} />
                </button>
            </div>
        </section>

        {/* ===== PRICING ===== */}
        <section className="wp-pricing" id="pricing" data-wp-reveal="pricing" aria-labelledby="wp-pricing-title">
            <div className={`wp-pricing__header ${revealClass('pricing')}`}>
                <h2 id="wp-pricing-title">Tarife {currentYear}</h2>
                <p>Alege planul potrivit. Toate planurile includ generare XML Intrastat.</p>
            </div>
            <div className="wp-pricing__grid" data-wp-reveal="pricegrid">
                <div className={`wp-pricing__cards ${revealClass('pricegrid')}`}>
                    {renderPricingCard(DEMO_PLAN, -1, true)}
                    {totalSubs.map((sub, i) => {
                        if (i === totalSubs.length - 1) return null
                        return renderPricingCard(sub, i)
                    })}
                </div>
            </div>

            <div className="wp-pricing__enterprise" data-wp-reveal="enterprise">
                <div className={`wp-pricing__enterprise-inner ${revealClass('enterprise')}`}
                     onClick={() => navigate('/register')}
                     role="button"
                     tabIndex={0}
                     onKeyDown={(e) => e.key === 'Enter' && navigate('/register')}
                >
                    <div className="wp-pricing__enterprise-text">
                        <h3>Professional — pentru declaranți terți</h3>
                        <p>10 firme, {formatNr(totalSubs[totalSubs.length - 1].lines)} linii/lună, 10 machete, toate funcționalitățile</p>
                    </div>
                    <div className="wp-pricing__enterprise-price">
                        <span className="wp-pricing__enterprise-old">€{getOldPrice(totalSubs[totalSubs.length - 1].price)}/lună</span>
                        <span className="wp-pricing__enterprise-amount">€{totalSubs[totalSubs.length - 1].price}/lună</span>
                    </div>
                    <FontAwesomeIcon icon={faArrowRight} className="wp-pricing__enterprise-arrow" />
                </div>
            </div>

            <div className="wp-pricing__notes">
                <small>Pentru oferte personalizate vă rugăm să ne contactați.</small>
                <small>Plata se efectuează o dată pe an (câte 12 luni). Prețurile nu includ TVA.</small>
                {/* The prices are quoted in EUR but the card is charged in RON - the currency
                    actually transacted in has to be stated before the customer commits */}
                <small>
                    Prețurile sunt afișate în <b>EUR</b>. Facturarea și plata se efectuează în
                    <b> LEI (RON)</b>, la cursul BNR, cu TVA inclus. Suma exactă în lei este afișată
                    înainte de confirmarea plății.
                </small>
                <small>Plata online cu cardul este procesată securizat de NETOPIA Payments (Visa, Mastercard).</small>
            </div>
        </section>

        {/* ===== ABOUT INTRASTAT (SEO) ===== */}
        <section className="wp-about" id="despre-intrastat" data-wp-reveal="about" aria-labelledby="wp-about-title">
            <div className={`wp-about__inner ${revealClass('about')}`}>
                <h2 id="wp-about-title" title={`Despre Intrastat ${currentYear}`}>Despre Intrastat {currentYear}</h2>

                <div className="wp-about__grid">
                    <article className="wp-about__article" id="ce-este-intrastat">
                        <h3 title="Ce este Intrastat">Ce este Intrastat?</h3>
                        <p>
                            Intrastat este sistemul de colectare a datelor statistice privind comerțul cu bunuri între statele
                            membre ale Uniunii Europene. Acest sistem statistic este operațional de la 1 ianuarie 1993 la nivelul UE.
                        </p>
                    </article>

                    <article className="wp-about__article" id="cine-declara-intrastat">
                        <h3 title="Cine declară Intrastat">Cine declară Intrastat?</h3>
                        <p>Obligația furnizării datelor pentru sistemul statistic Intrastat revine operatorilor economici care:</p>
                        <ul>
                            <li>sunt înregistrați în scopuri de TVA (au cod de identificare fiscală)</li>
                            <li>realizează schimburi de bunuri cu alte state membre UE</li>
                            <li>depășesc pragurile valorice Intrastat stabilite anual pentru introduceri și expedieri</li>
                        </ul>
                        <p>Operatorii economici trebuie să transmită lunar la INS, în format electronic, declarația statistică privind:</p>
                        <ul>
                            <li><strong>Introduceri:</strong> sosirea bunurilor în România din statele membre UE</li>
                            <li><strong>Expedieri:</strong> ieșirea bunurilor din România către statele membre UE</li>
                        </ul>
                    </article>

                    <article className="wp-about__article" id="cand-trebuie-transmis-intrastat">
                        <h3>Când trebuie transmis Intrastat?</h3>
                        <p>
                            Termenul limită de transmitere a datelor Intrastat la INS este <strong>data de 15 a lunii următoare
                            celei de referință</strong>.
                        </p>
                    </article>
                </div>

                <div className="wp-about__links">
                    <a href="http://www.intrastat.ro/di.php" target="_blank" rel="noreferrer">
                        Site-ul oficial INTRASTAT
                    </a>
                    <a href="https://landing.programintrastat.ro/" target="_blank" rel="noopener noreferrer">
                        Mai multe informații despre platformă
                    </a>
                </div>
            </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section className="wp-final-cta" data-wp-reveal="final" aria-label="Creare cont">
            <div className={`wp-final-cta__inner ${revealClass('final')}`}>
                <h2>Generează declarația Intrastat {currentYear}</h2>
                <p>3 luni gratuit. Toate funcționalitățile. Fără card bancar.</p>
                <button className="wp-btn wp-btn--primary wp-btn--lg" onClick={() => navigate('/register')}>
                    Creează cont gratuit
                    <FontAwesomeIcon icon={faArrowRight} />
                </button>
            </div>
        </section>

    </main>

    {modal &&
        <ModalSetValue
        values = {{type: 0}}
        setValue = {false}
        close = {() => setModal(false)}
    />}

    <ReactTooltip effect="solid" id='pricing-not-available' type='error'>Nu conține</ReactTooltip>
    <ReactTooltip effect="solid" id='pricing-available' type='success'>Conține</ReactTooltip>
    </>
  )
}

export default WeclomePage